import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ errorInfo });

    // Detect error type
    const errorMessage = error.message || '';
    const errorStack = error.stack || '';
    let errorType = 'Unknown Error';
    let location = 'Unknown';

    // Detect infinite loop errors
    if (errorMessage.includes('Maximum update depth exceeded') || 
        errorMessage.includes('Maximum call stack size exceeded')) {
      errorType = 'Infinite Loop / Maximum Update Depth';
      
      // Try to extract component name from component stack
      const componentStack = errorInfo.componentStack || '';
      const componentMatch = componentStack.match(/^\s*in\s+(\w+)/m);
      if (componentMatch) {
        location = `Component: ${componentMatch[1]}`;
      }
      
      // Try to extract from error stack
      const stackMatch = errorStack.match(/at\s+(\w+)/);
      if (stackMatch && location === 'Unknown') {
        location = `Function: ${stackMatch[1]}`;
      }
    } else if (errorMessage.includes('Cannot read') || errorMessage.includes('undefined')) {
      errorType = 'Null/Undefined Reference';
    } else if (errorMessage.includes('Cannot access')) {
      errorType = 'Access Error';
    } else if (errorStack.includes('useEffect') || errorStack.includes('useState')) {
      errorType = 'React Hook Error';
    }

    // Extract more location info from component stack
    if (errorInfo.componentStack) {
      const lines = errorInfo.componentStack.split('\n').slice(0, 3);
      if (lines.length > 0 && location === 'Unknown') {
        location = lines.join(' → ');
      }
    }

    // Log to console (visible in DevTools)
    console.error('========== ErrorBoundary caught an error ==========');
    console.error('Error Type:', errorType);
    console.error('Message:', error.message);
    console.error('Location:', location);
    console.error('Full Error:', error);
    console.error('Component Stack:', errorInfo.componentStack);
    console.error('Error Stack:', error.stack);
    console.error('==================================================');

    // Send to main process for terminal logging (Electron)
    if (window.electronWindow && typeof window.electronWindow.logError === 'function') {
      try {
        window.electronWindow.logError({
          message: error.message,
          stack: error.stack,
          componentStack: errorInfo.componentStack,
          errorType,
          location,
        }).catch((err: any) => {
          console.error('Failed to log error to main process:', err);
        });
      } catch (logErr) {
        console.error('Error calling logError:', logErr);
      }
    }
  }

  private getErrorDetails() {
    const { error, errorInfo } = this.state;
    if (!error) return null;

    const errorMessage = error.message || '';
    const isInfiniteLoop = errorMessage.includes('Maximum update depth exceeded') || 
                          errorMessage.includes('Maximum call stack size exceeded');

    return {
      isInfiniteLoop,
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo?.componentStack,
    };
  }

  public render() {
    if (this.state.hasError) {
      const errorDetails = this.getErrorDetails();
      const { error } = this.state;

      return (
        <div className="flex h-full items-center justify-center bg-black text-white p-8">
          <div className="max-w-4xl w-full">
            <div className="bg-red-900/20 border border-red-500/50 rounded-lg p-6 mb-4">
              <h2 className="text-2xl font-bold mb-4 text-red-400">Something went wrong!</h2>
              
              {errorDetails?.isInfiniteLoop && (
                <div className="bg-yellow-900/30 border border-yellow-500/50 rounded p-4 mb-4">
                  <p className="text-yellow-300 font-semibold mb-2"> Infinite Loop Detected</p>
                  <p className="text-yellow-200 text-sm">
                    This usually happens when a component repeatedly calls setState inside useEffect or 
                    when dependencies in useEffect cause infinite re-renders. Check the component stack below 
                    to identify which component is causing the issue.
                  </p>
                </div>
              )}

              <div className="space-y-3 mb-4">
                <div>
                  <p className="text-gray-300 font-semibold mb-1">Error Message:</p>
                  <p className="text-red-300 font-mono text-sm break-words">{error?.message}</p>
                </div>

                {errorDetails?.componentStack && (
                  <div>
                    <p className="text-gray-300 font-semibold mb-1">Component Stack (where error occurred):</p>
                    <pre className="text-gray-400 text-xs bg-black/40 p-3 rounded overflow-auto max-h-40 font-mono">
                      {errorDetails.componentStack}
                    </pre>
                  </div>
                )}

                {errorDetails?.stack && (
                  <details className="mt-4">
                    <summary className="text-gray-300 font-semibold cursor-pointer hover:text-white">
                      Stack Trace (click to expand)
                    </summary>
                    <pre className="text-gray-400 text-xs bg-black/40 p-3 rounded overflow-auto max-h-60 font-mono mt-2">
                      {errorDetails.stack}
                    </pre>
                  </details>
                )}
              </div>

              <div className="flex gap-3">
                <button 
                  onClick={() => {
                    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
                    // Reload the page to ensure clean state
                    window.location.reload();
                  }}
                  className="bg-[#4da3ff] hover:bg-[#3a8bcc] text-white px-4 py-2 rounded"
                >
                  Reload Page
                </button>
                <button 
                  onClick={() => this.setState({ hasError: false, error: undefined, errorInfo: undefined })}
                  className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded"
                >
                  Try Again (may not fix infinite loops)
                </button>
              </div>

              <p className="text-gray-500 text-xs mt-4">
                 Tip: Check the terminal/console where Electron is running for detailed error logs.
              </p>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
